import { duration, getFieldPhotoPath } from "common";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Field, Guest, Purchase, PurchaseItem } from "types";
import { fetchBlob } from "@/api/mediaApi";
import { xolaUri } from "@/api/xolaUri";
import { CreatePurchaseItemState } from "@/stores/createPurchaseStore";

export interface UsePhotoFieldOptions {
    field: Field;
    guest?: Guest;
    item?: PurchaseItem | CreatePurchaseItemState;
    purchase?: Purchase;
    isItemLevel?: boolean;
}

export const usePhotoField = ({ field, guest, item, purchase, isItemLevel = false }: UsePhotoFieldOptions) => {
    const fieldLabel = field.label;
    const [blobUrl, setBlobUrl] = useState<string>();

    const { photoPath, isItemLevelPhoto } = useMemo(() => {
        const itemFormResponse = item && "formResponse" in item ? item.formResponse : undefined;
        // When item-level, skip guest formResponse so photos always resolve via the item path
        // (itemLevelGuest.formResponse is set to purchaseItem.formResponse, which would otherwise
        // cause getFieldPhotoPath to return isItemLevelPhoto=false and miss the auth endpoint).
        const guestFormResponse = isItemLevel ? undefined : guest?.formResponse;
        return getFieldPhotoPath(fieldLabel, guestFormResponse, itemFormResponse);
    }, [fieldLabel, guest?.formResponse, isItemLevel, item]);

    // Fetch the image blob with authentication
    const {
        data: blob,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["questionnaire-photo", photoPath, purchase?.id, item?.id, field.label],
        queryFn: async () => {
            // For item-level photos, use authenticated endpoint (no fallback)
            if (isItemLevelPhoto && purchase?.id && item?.id) {
                const label = encodeURIComponent(field.label);
                const url = `${xolaUri}/api/purchases/${purchase.id}/items/${item.id}/formResponse/fields/${label}`;
                const { data } = await fetchBlob(url);
                return data;
            }
        },
        enabled: !!photoPath,
        staleTime: duration(30, "minutes"),
    });

    useEffect(() => {
        if (blob) {
            const url = URL.createObjectURL(blob);
            setBlobUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setBlobUrl(undefined);
    }, [blob]);

    return { photoPath, blob, blobUrl, isLoading, isError };
};
